import React from "react";
import { cn } from "../../styles";
import { mdiClose } from "@mdi/js";
import { LocalizationScope } from "../../util/LocalizationScope";
import { Button, ButtonVariant, Typography } from "../../atoms";
import { ThemeColor } from "../../props";
import { processErrorContent } from "./helper";
import { Input } from "../Input";
import { BaseFormFieldProps } from "./index";

/**
 * Format constraint configuration for text fields.
 */
export interface TextFieldConstraintFormat {
  /**
   * Input mask pattern for formatting.
   */
  mask?: string;
  /**
   * Example text showing the expected format.
   */
  example?: string;
}

/**
 * Extended constraint configuration specific to text fields.
 */
export interface TextFieldConstraint {
  presence?: boolean;
  format?: TextFieldConstraintFormat;
  [key: string]: any;
}

export interface FormFieldTextProps extends Omit<
  BaseFormFieldProps,
  "constraints"
> {
  /**
   * Current value of the text field.
   */
  value: string | null;
  /**
   * Callback function called when the field value changes.
   */
  onChangeField: (id: string, value: string | null) => void;
  /**
   * Validation constraints specific to text fields.
   */
  constraints: TextFieldConstraint;
  /**
   * Placeholder text shown when the field is empty.
   */
  placeholder?: string;
  /**
   * Automatically focuses the field when mounted.
   * @default false
   */
  autoFocus?: boolean;
  /**
   * Callback function called when Ctrl+Enter is pressed.
   * Enables quick form submission.
   */
  onQuickSubmit?: () => void;
  /**
   * Specifies the key combination for quick submission.
   * @default "enter+ctrl"
   */
  quickSubmitKey?: "enter+ctrl" | "enter+shift" | "enter+alt" | "enter";
  /**
   * Automatically selects all text when the field receives focus.
   * @default false
   */
  autoSelectAll?: boolean;
  /**
   * Custom element to display at the end of the input field.
   * Replaces the default clear button when provided.
   */
  endAdornment?: React.ReactNode;
  /**
   * HTML input type attribute (text, email, password, etc.).
   * @default "text"
   */
  inputType?: React.HTMLInputTypeAttribute;
  /**
   * Shows a clear button when the field has a value and is not required.
   * @default true
   */
  clearable?: boolean;
  /**
   * Applies bold font weight to the input text.
   * @default false
   */
  strong?: boolean;
  /**
   * Text to display before the input value.
   */
  prefix?: string;
  /**
   * Text to display after the input value.
   */
  postfix?: string;
}

export const Text: React.FC<FormFieldTextProps> = ({
  className,
  id,
  value,
  onChangeField,
  label = "Label",
  localKey,
  errors = [],
  touched,
  helper,
  helperKey,
  constraints,
  placeholder,
  autoFocus,
  disabled,
  onQuickSubmit,
  quickSubmitKey = "enter+ctrl",
  autoSelectAll,
  keepHeight,
  noLabel,
  endAdornment,
  inputType = "text",
  clearable = true,
  strong,
  prefix,
  postfix,
  dense,
  readOnly,
}) => {
  const showClear =
    !disabled && !readOnly && clearable && !constraints.presence && !!value;
  const error = touched && errors[0];

  const handleQuickSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || e.metaKey || !onQuickSubmit) return;

    const pressedModifiers = {
      ctrl: e.ctrlKey,
      shift: e.shiftKey,
      alt: e.altKey,
    };
    const expectedModifiers = {
      ctrl: quickSubmitKey === "enter+ctrl",
      shift: quickSubmitKey === "enter+shift",
      alt: quickSubmitKey === "enter+alt",
    };

    if (
      pressedModifiers.ctrl === expectedModifiers.ctrl &&
      pressedModifiers.shift === expectedModifiers.shift &&
      pressedModifiers.alt === expectedModifiers.alt
    ) {
      onQuickSubmit();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleChange = (value: string) => onChangeField(id, value);
  const handleClear = () => onChangeField(id, null);

  return (
    <div className={className}>
      {!noLabel && (
        <div className="flex">
          <Typography.Label
            htmlFor={id}
            className={cn(
              "block font-medium select-none",
              dense ? "mb-0.5 text-xs" : "mb-1 text-sm",
            )}
            content={label || { local: id }}
          />
          {!!constraints.presence && (
            <span className="text-red-500 ml-0.5 text-xs align-super">*</span>
          )}
        </div>
      )}
      <div className="flex gap-2 items-center">
        <Input
          className="grow"
          id={id}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          readOnly={readOnly}
          onKeyDown={onQuickSubmit ? handleQuickSubmit : undefined}
          onFocus={autoSelectAll ? handleFocus : undefined}
          inputType={inputType}
          required={!!constraints.presence}
          strong={strong}
          dense={dense}
          error={!!error}
          prefix={prefix}
          postfix={postfix}
          mask={constraints.format?.mask}
          aria-invalid={!!error}
          aria-describedby={
            helper ? `${id}-helper` : error ? `${id}-error` : undefined
          }
        />
        {endAdornment
          ? endAdornment
          : showClear && (
              <Button
                variant={ButtonVariant.text}
                color={ThemeColor.gray}
                onClick={handleClear}
                iconPath={mdiClose}
                dense
                muted
                tabIndex={-1}
                disabled={disabled}
              />
            )}
      </div>
      {(helperKey || helper || keepHeight) && !error && (
        <LocalizationScope scope={localKey}>
          <Typography.P
            id={`${id}-helper`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.gray}
            content={helperKey ? { local: helperKey } : helper ? helper : null}
          >
            {" "}
          </Typography.P>
        </LocalizationScope>
      )}
      {error && (
        <LocalizationScope scope={[localKey, "::PSS"]}>
          <Typography.P
            id={`${id}-error`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.error}
            content={processErrorContent(error)}
          />
        </LocalizationScope>
      )}
    </div>
  );
};
